/* Ajustes: Configuración (/config-ticket) y Tickets (/config-tickets), con la
   organización por grupos de siempre y el estilo del rediseño. Equipo es su
   propia página (/usuarios). Este archivo solo arma la navegación, sostiene el
   objeto `config` y lo guarda; cada ajuste vive en ../config. */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { generarTicketHTML } from '../../utils/ticket';
import { aplicarTema, coloresDeConfig } from '../../utils/tema';
import { refrescarConfigNegocio } from '../../hooks/useConfigNegocio';
import { useToast } from '../../components/ui/Toast';
import { btnPrimario, segmento, segmentos } from '../../components/ui/estilos';
import { entrada } from '../agenda/campos';
import {
  SeccionIdentidad, SeccionApariencia, SeccionMesas, SeccionMetodosPago,
  SeccionComisiones, SeccionInventario, SeccionImpresion, SeccionFacturacion, SeccionKds,
  SeccionReservaciones, SeccionAppMesero, SeccionBotonesCaja, SeccionMovimientosCaja, SeccionProductos, SeccionObjetivos, SeccionComidaEmpleados,
} from '../config/SeccionesConfig';
import {
  SeccionTicketVenta, SeccionPrecuenta, SeccionCocina, SeccionBarra, SeccionCorte, SeccionPropinas,
} from '../config/SeccionesTickets';
import { TICKET_DEMO, mockupComanda, mockupPrecuenta, mockupCorte, mockupPropinas } from '../config/mockups';
import SeccionMercadoPago from '../config/SeccionMercadoPago';

type Props = { config: any; actualizar: (k: string, v: string) => void };
type DefSeccion = {
  id: string; label: string; descripcion: string;
  buscar: string;   // palabras con las que se encuentra desde el buscador
  Componente: React.ComponentType<Props>;
};
type Grupo = { label: string; secciones: DefSeccion[] };
type Pestana = { id: string; label: string; grupos: Grupo[] };

// Misma organización que antes del rediseño: Configuración (el negocio y su
// operación) y Tickets (lo que se imprime). Equipo es su propia página.
const PESTANAS: Pestana[] = [
  {
    id: 'config', label: 'Configuración', grupos: [
      { label: 'Salón y meseros', secciones: [
        { id: 'mesas', label: 'Mesas', descripcion: 'Alta, edición y baja de las mesas del salón y su zona. El acomodo del plano se hace en Salón › Acomodar plano.', buscar: 'mesas capacidad comensales salon zona terraza', Componente: SeccionMesas },
        { id: 'reservaciones', label: 'Reservaciones', descripcion: 'Reglas para aceptar reservaciones de mesa.', buscar: 'reservacion reserva margen encimada minutos', Componente: SeccionReservaciones },
        { id: 'app_mesero', label: 'App del mesero', descripcion: 'Seguridad del teléfono y límites de lo que escribe el mesero.', buscar: 'telefono celular bloqueo pin huella nota mesero', Componente: SeccionAppMesero },
      ] },
      { label: 'Cocina y barra', secciones: [
        { id: 'kds', label: 'KDS', descripcion: 'Colores, sonido y tiempos de la pantalla de preparación (Comandas), en tablet y PC.', buscar: 'kds cocina barra alarma sonido volumen recordatorio colores comandas', Componente: SeccionKds },
      ] },
      { label: 'Caja y cobro', secciones: [
        { id: 'metodos_pago', label: 'Métodos de pago', descripcion: 'Con qué se puede cobrar en Cuentas y con qué clave del SAT se factura cada uno.', buscar: 'pago efectivo tarjeta transferencia qr cfdi sat', Componente: SeccionMetodosPago },
        { id: 'mercado_pago', label: 'Mercado Pago Point', descripcion: 'Conexión con tu Point Smart 2: credenciales, terminal y activación de cobros.', buscar: 'mercado pago mercadopago point smart 2 terminal access token conectar', Componente: SeccionMercadoPago },
        { id: 'botones_caja', label: 'Propinas y redondeo', descripcion: 'Porcentajes de propina y descuento que aparecen como botones al cobrar, y redondeo de totales.', buscar: 'propina descuento porcentaje redondeo redondear centavos boton rapido', Componente: SeccionBotonesCaja },
        { id: 'comisiones', label: 'Comisiones de terminal', descripcion: 'Lo que se queda la terminal por cada cobro con tarjeta, para que el corte muestre lo que de verdad cae a tu cuenta.', buscar: 'comision terminal tarjeta clip mercado pago banco tasa', Componente: SeccionComisiones },
        { id: 'movimientos_caja', label: 'Movimientos de caja', descripcion: 'Los conceptos para clasificar retiros y depósitos de efectivo.', buscar: 'movimiento retiro deposito categoria concepto caja gasto', Componente: SeccionMovimientosCaja },
      ] },
      { label: 'Productos e inventario', secciones: [
        { id: 'productos', label: 'Productos', descripcion: 'Valores por defecto al dar de alta un producto del menú.', buscar: 'producto iva tasa frontera unidades menu', Componente: SeccionProductos },
        { id: 'inventario', label: 'Inventario', descripcion: 'Unidades de insumos y lo que las cuentas abiertas tienen apartado, con el botón para volver a contarlo.', buscar: 'inventario insumos unidades apartado reservado stock recalcular', Componente: SeccionInventario },
        { id: 'comida_empleados', label: 'Comida de empleados', descripcion: 'Límite de comidas por día. Se aprueban con la contraseña de quien tenga el permiso en Equipo.', buscar: 'comida empleados personal limite dia autorizar', Componente: SeccionComidaEmpleados },
      ] },
      { label: 'Objetivos SMART', secciones: [
        { id: 'objetivos', label: 'Objetivos SMART', descripcion: 'Categorías, unidades y actualización de la pantalla de objetivos.', buscar: 'objetivos smart metas categorias unidades', Componente: SeccionObjetivos },
      ] },
      { label: 'Equipo y facturación', secciones: [
        { id: 'impresion', label: 'Impresoras y cajón', descripcion: 'Hardware del punto de venta: ancho de papel, dirección de cada impresora y cajón de dinero.', buscar: 'impresora ip red usb papel 80mm 58mm cajon escpos 9100', Componente: SeccionImpresion },
        { id: 'facturacion', label: 'Facturación', descripcion: 'Facturación electrónica (CFDI 4.0): credenciales, datos del emisor y envío por correo.', buscar: 'factura cfdi sat facturama rfc regimen smtp correo timbrar', Componente: SeccionFacturacion },
        { id: 'apariencia', label: 'Apariencia', descripcion: 'Tema claro u oscuro y color de marca del panel y de la app del mesero.', buscar: 'tema oscuro claro color marca branding', Componente: SeccionApariencia },
      ] },
    ],
  },
  {
    id: 'tickets', label: 'Tickets', grupos: [
      { label: 'Para el cliente', secciones: [
        { id: 'identidad', label: 'Identidad', descripcion: 'Nombre, datos de contacto y logo. Es lo que ve el cliente en el ticket y lo que aparece en el panel.', buscar: 'nombre restaurante slogan direccion telefono rfc logo imagen', Componente: SeccionIdentidad },
        { id: 'venta', label: 'Ticket de venta', descripcion: 'El comprobante que se entrega al cliente cuando paga.', buscar: 'ticket venta iva cambio propina lineas imagen qr pie', Componente: SeccionTicketVenta },
        { id: 'precuenta', label: 'Pre-cuenta', descripcion: 'El detalle de consumo que se lleva a la mesa antes de cobrar.', buscar: 'precuenta cuenta previa mesa precios', Componente: SeccionPrecuenta },
      ] },
      { label: 'Para producción', secciones: [
        { id: 'cocina', label: 'Comanda de cocina', descripcion: 'Lo que sale por la impresora de cocina al enviar una comanda.', buscar: 'cocina comanda mesero hora', Componente: SeccionCocina },
        { id: 'barra', label: 'Comanda de barra', descripcion: 'Lo que sale por la impresora de barra al enviar una comanda.', buscar: 'barra comanda bebidas mesero', Componente: SeccionBarra },
      ] },
      { label: 'Al cerrar turno', secciones: [
        { id: 'corte', label: 'Corte de turno', descripcion: 'El reporte que se imprime al cerrar la caja.', buscar: 'corte turno caja formas pago declaracion firmas', Componente: SeccionCorte },
        { id: 'reporte_propinas', label: 'Propinas', descripcion: 'El desglose de propinas por mesero al cierre del turno.', buscar: 'propinas meseros firma reparto', Componente: SeccionPropinas },
      ] },
    ],
  },
];
const seccionesDe = (p: Pestana) => p.grupos.flatMap(g => g.secciones);
export const RUTA_AJUSTES: Record<string, string> = { config: '/config-ticket', tickets: '/config-tickets' };

/* Vista previa en papel, regenerada con cada cambio. */
function previa(id: string, config: any): string {
  switch (id) {
    case 'identidad':
    case 'venta':            return generarTicketHTML({ ...TICKET_DEMO, ...config } as any);
    case 'precuenta':        return mockupPrecuenta(config.precuenta_titulo, config.precuenta_mensaje, config.precuenta_mostrar_precios ?? 'true', config.restaurante || '');
    case 'cocina':           return mockupComanda('COCINA', config.cocina_titulo, config.cocina_mostrar_mesero, config.cocina_mostrar_hora);
    case 'barra':            return mockupComanda('BARRA', config.barra_titulo, config.barra_mostrar_mesero, 'true');
    case 'corte':            return mockupCorte(config);
    case 'reporte_propinas': return mockupPropinas(config.propinas_titulo, config.propinas_mostrar_firma ?? 'true');
    default:                 return '';
  }
}

export default function Ajustes({ grupo }: { grupo: 'config' | 'tickets' }) {
  const pestana = PESTANAS.find(p => p.id === grupo)!;
  return <AjustesPestana key={pestana.id} pestana={pestana} />;
}

function AjustesPestana({ pestana }: { pestana: Pestana }) {
  const navigate = useNavigate();
  const [toast, say] = useToast();
  const [config, setConfig] = useState<any>({});
  const [activa, setActiva] = useState(() => {
    const h = window.location.hash.slice(1);
    return seccionesDe(pestana).some(s => s.id === h) ? h : seccionesDe(pestana)[0].id;
  });
  const [busqueda, setBusqueda] = useState('');
  const [guardando, setGuardando] = useState(false);
  const guardado = useRef('{}'); // lo último que confirmó el servidor

  useEffect(() => {
    api.get('/configuracion').then(r => { setConfig(r.data); guardado.current = JSON.stringify(r.data); })
      .catch(() => say('No se pudieron cargar los ajustes.'));
  }, [say]);

  // La previa de tema se aplica al instante; si se sale sin guardar, vuelve la guardada.
  useEffect(() => () => { const g = JSON.parse(guardado.current); aplicarTema(g.tema || 'claro', coloresDeConfig(g)); }, []);

  const actualizar = (k: string, v: string) => setConfig((c: any) => ({ ...c, [k]: v }));
  const hayCambios = JSON.stringify(config) !== guardado.current;

  const guardar = async () => {
    setGuardando(true);
    try {
      const { data } = await api.put('/configuracion', config);
      setConfig(data); guardado.current = JSON.stringify(data); refrescarConfigNegocio(data);
      say('Ajustes guardados.');
    } catch (e: any) { say(e.response?.data?.error || 'No se pudieron guardar los ajustes.'); }
    finally { setGuardando(false); }
  };

  // El buscador recorre todas las pestañas: un ajuste se encuentra aunque viva en otra.
  const filtro = busqueda.trim().toLowerCase();
  const resultados = useMemo(() => !filtro ? [] : PESTANAS.flatMap(p => seccionesDe(p)
    .filter(s => `${s.label} ${s.descripcion} ${s.buscar}`.toLowerCase().includes(filtro))
    .map(s => ({ p, s }))), [filtro]);

  const seccion = seccionesDe(pestana).find(s => s.id === activa) || seccionesDe(pestana)[0];
  const html = useMemo(() => previa(seccion.id, config), [seccion.id, config]);
  const Contenido = seccion.Componente;

  const irA = (p: Pestana, s: DefSeccion) => {
    setBusqueda('');
    if (p.id === pestana.id) setActiva(s.id);
    else navigate(`${RUTA_AJUSTES[p.id]}#${s.id}`);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, maxWidth: 1400 }}>
      <header style={{ position: 'sticky', top: -22, zIndex: 5, margin: '-22px -24px 0', padding: '14px 24px', background: 'var(--surface)', borderBottom: '1px solid var(--line)',
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
        <div role="tablist" aria-label="Ajustes" style={{ ...segmentos, overflowX: 'auto', maxWidth: '100%' }}>
          {PESTANAS.map(p => (
            <button key={p.id} role="tab" aria-selected={p.id === pestana.id} onClick={() => navigate(RUTA_AJUSTES[p.id])}
              style={{ ...segmento(p.id === pestana.id), height: 40, fontSize: 15, whiteSpace: 'nowrap' }}>{p.label}</button>
          ))}
          <button role="tab" aria-selected={false} onClick={() => navigate('/usuarios')} style={{ ...segmento(false), height: 40, fontSize: 15 }}>Equipo</button>
        </div>
        <div style={{ flex: 1 }} />
        {hayCambios && <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--warn)' }}>Cambios sin guardar</span>}
        <button onClick={guardar} disabled={guardando || !hayCambios}
          style={{ ...btnPrimario, height: 48, fontSize: 16, opacity: guardando || !hayCambios ? .5 : 1, cursor: hayCambios ? 'pointer' : 'default' }}>
          {guardando ? 'Guardando…' : 'Guardar cambios'}
        </button>
      </header>

      <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <nav aria-label={`Secciones de ${pestana.label}`} style={{ width: 230, flex: 'none', position: 'sticky', top: 84, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <input value={busqueda} onChange={e => setBusqueda(e.target.value)} placeholder="Buscar ajuste…" aria-label="Buscar ajuste" style={{ ...entrada, marginBottom: 8 }} />
          {filtro ? <>
            {resultados.map(({ p, s }) => (
              <button key={p.id + s.id} onClick={() => irA(p, s)} className="nav-item" style={itemNav(false)}>
                {s.label}<span style={{ display: 'block', fontSize: 12, color: 'var(--muted)', fontWeight: 400 }}>{p.label}</span>
              </button>
            ))}
            {!resultados.length && <p style={{ fontSize: 14, color: 'var(--muted)', padding: '0 12px' }}>Nada coincide con “{busqueda}”.</p>}
          </> : pestana.grupos.map(g => (
            <div key={g.label} role="group" aria-label={g.label} style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--muted)', padding: '0 12px 4px' }}>{g.label}</div>
              {g.secciones.map(s => (
                <button key={s.id} onClick={() => setActiva(s.id)} aria-current={s.id === seccion.id || undefined} className="nav-item" style={itemNav(s.id === seccion.id)}>{s.label}</button>
              ))}
            </div>
          ))}
        </nav>

        <section aria-label={seccion.label} style={{ flex: '1 1 420px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>{seccion.label}</h2>
            <p style={{ margin: '4px 0 0', fontSize: 15, color: 'var(--muted)', lineHeight: 1.5 }}>{seccion.descripcion}</p>
          </div>
          <Contenido config={config} actualizar={actualizar} />
        </section>

        {html && (
          <aside aria-label="Vista previa" style={{ width: 300, flex: 'none', position: 'sticky', top: 84 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--muted)', marginBottom: 8 }}>VISTA PREVIA</div>
            <div style={{ border: '1px solid var(--line)', borderRadius: 16, overflow: 'hidden', background: '#fff' }}>
              <iframe srcDoc={html} title="Vista previa del ticket" style={{ width: '100%', height: 460, border: 'none', display: 'block' }} />
            </div>
            <p style={{ fontSize: 13, color: 'var(--muted)', margin: '8px 0 0', lineHeight: 1.45 }}>Aproximada: la impresora térmica usa su propia letra.</p>
          </aside>
        )}
      </div>
      {toast}
    </div>
  );
}

const itemNav = (on: boolean): React.CSSProperties => ({
  minHeight: 44, textAlign: 'left', padding: '8px 12px', border: 'none', borderRadius: 9, fontSize: 15, fontWeight: 700, cursor: 'pointer',
  background: on ? 'var(--ink)' : 'transparent', color: on ? 'var(--bg)' : 'var(--ink)',
});
